<template>
  <v-container fluid class="subscription-page" :class="{ embedded: isEmbedded }">
    <Create
      :createDialog="createDialog"
      :subscription="selectedSubscription"
      @close="createDialog = false"
      @submit="getList"
      :donor="DonorId"
    ></Create>
    <div v-if="!isEmbedded" class="subscription-page-heading">
      <div>
        <div class="page-eyebrow">Billing</div>
        <h1>Subscription</h1>
        <p>Create and manage templates that automatically generate future invoices.</p>
      </div>
      <v-btn v-if="checkRightStatus(61)" color="primary" depressed @click="add">
        <v-icon left>mdi-plus</v-icon>
        New template
      </v-btn>
    </div>

    <v-card :loading="loading" elevation="0" class="subscription-card">
      <div class="subscription-summary">
        <div class="summary-item">
          <strong>{{ activeCount }}</strong
          ><span>Active templates</span>
        </div>
        <div class="summary-item">
          <strong>{{ expiringCount }}</strong
          ><span>Ended templates</span>
        </div>
        <div class="summary-item">
          <strong>{{ recurringTotal | currency }}</strong
          ><span>Recurring value</span>
        </div>
        <v-spacer />
        <v-btn v-if="isEmbedded && checkRightStatus(61)" color="primary" depressed @click="add">
          <v-icon left>mdi-plus</v-icon>
          New template
        </v-btn>
      </div>

      <div class="subscription-filters">
        <v-text-field
          v-model="search"
          outlined
          prepend-inner-icon="mdi-magnify"
          dense
          label="Search templates"
          placeholder="Parent, donor, or amount"
          single-line
          hide-details
          clearable
        />
        <v-btn-toggle
          v-if="toggleStatus === 'show'"
          v-model="toggleChange"
          mandatory
          color="primary"
          class="audience-toggle"
        >
          <v-btn small value="all">All</v-btn>
          <v-btn small value="parents"
            ><v-icon left small>mdi-account-outline</v-icon>Parents</v-btn
          >
          <v-btn small value="donors"
            ><v-icon left small>mdi-hand-coin-outline</v-icon>Donors</v-btn
          >
        </v-btn-toggle>
        <v-switch
          v-model="showExpire"
          color="primary"
          inset
          hide-details
          label="Include ended"
        />
      </div>

      <v-data-table
        class="subscription-table"
        :headers="headers"
        :items="Subscriptions"
        :search="search"
        :sort-desc="sortDesc"
        :loading="loading"
        :sort-by="sortBy"
        :page.sync="page"
        :items-per-page="itemsPerPage"
      >
        <template v-slot:item="{ item }">
          <tr class="subscription-row">
            <td class="text-left" data-label="Subscriber">
              <button
                type="button"
                class="subscriber-link"
                @click="viewProfileNewTab(item)"
              >
                <v-avatar size="34" color="green lighten-5" class="mr-3">
                  <v-icon small color="primary">{{
                    item.Customer
                      ? "mdi-account-outline"
                      : "mdi-hand-coin-outline"
                  }}</v-icon>
                </v-avatar>
                <span
                  ><strong>{{ item.name }}</strong
                  ><small>{{ item.Customer ? "Parent" : "Donor" }}</small></span
                >
                <v-icon x-small color="grey" class="ml-1"
                  >mdi-open-in-new</v-icon
                >
              </button>
            </td>
            <td
              class="text-left"
              data-label="Billing method"
              @click="viewSubscription(item)"
            >
              <v-chip small outlined color="primary" label>
                <v-icon left small>{{ paymentMethodIcon(item) }}</v-icon
                >{{ paymentMethodLabel(item) }}
              </v-chip>
            </td>
            <td
              class="text-left"
              data-label="Next invoice"
              @click="viewSubscription(item)"
            >
              <template v-if="isRecurringExpired(item)">
                <div class="date-primary">
                  {{ formattedNextInvoiceDate(item.nextInvoiceDate) }}
                </div>
                <div class="date-secondary">Next invoice</div>
              </template>
              <v-chip
                v-else
                small
                label
                color="grey lighten-3"
                text-color="grey darken-2"
                >Ended</v-chip
              >
            </td>
            <td
              class="text-left"
              data-label="Duration"
              @click="viewSubscription(item)"
            >
              <div class="date-primary">
                Started {{ item.startingDate | moment("MMM D, YYYY") }}
              </div>
              <div class="date-secondary">{{ durationLabel(item) }}</div>
            </td>
            <td
              class="text-right template-total"
              data-label="Invoice amount"
              @click="viewSubscription(item)"
            >
              {{ item.total | currency }}
            </td>
            <td
              class="justify-end align-center text-right action-cell"
              data-label="Actions"
            >
              <v-menu bottom left offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    icon
                    aria-label="Subscription actions"
                  >
                    <v-icon>mdi-dots-horizontal</v-icon>
                  </v-btn>
                </template>
                <v-list class="menu-button">
                  <v-list-item
                    v-if="!DonorId && !CustomerId"
                    @click="viewProfile(item)"
                  >
                    <v-list-item-title>Profile</v-list-item-title>
                  </v-list-item>
                  <v-divider v-if="!DonorId && !CustomerId"></v-divider>
                  <v-list-item @click="viewSubscription(item)">
                    <v-list-item-title>View template</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="collectedPayment(item)">
                    <v-list-item-title
                      >Collect Payment Information</v-list-item-title
                    >
                  </v-list-item>
                  <v-divider></v-divider>
                  <v-list-item
                    v-if="checkRightStatus(63)"
                    @click="editSubscription(item)"
                  >
                    <v-list-item-title>Edit template</v-list-item-title>
                  </v-list-item>
                  <v-list-item
                    v-if="checkRightStatus(64)"
                    @click="deleteSubscription(item)"
                  >
                    <v-list-item-title>Delete template</v-list-item-title>
                  </v-list-item>
                  <v-list-item
                    v-if="isRecurringExpired(item)"
                    @click="createInvoice(item)"
                  >
                    <v-list-item-title>Create Invoice</v-list-item-title>
                  </v-list-item>

                  <v-list-item
                    v-if="isRecurringExpired(item)"
                    @click="createInvoiceAndCollect(item)"
                  >
                    <v-list-item-title
                      >Create Invoice & Collect</v-list-item-title
                    >
                  </v-list-item>
                </v-list>
              </v-menu>
            </td>
          </tr>
        </template>
        <template v-slot:no-data>
          <div class="empty-state py-12">
            <v-icon size="48" color="grey lighten-1"
              >mdi-calendar-sync-outline</v-icon
            >
            <strong>No subscription templates</strong
            ><span>Create a template to schedule recurring invoices.</span>
          </div>
        </template>
        <template v-slot:no-results>
          <div class="empty-state py-12">
            <strong>No matching templates</strong
            ><span>Try another parent, donor, or amount.</span>
          </div>
        </template>
      </v-data-table>
    </v-card>
    <v-dialog v-model="loadingDialog" persistent width="300">
      <v-card class="pa-5 text-center">
        <v-progress-circular indeterminate color="primary" class="mb-3" />
        <div class="font-weight-bold">Processing subscription</div>
        <div class="caption text--secondary">Please keep this window open.</div>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import subscriptionService from "../service";
const Create = () => import("@/modules/Donors/PaymentInfoCollect/Create.vue");
// import ShareLink from "@/components/ShareLink";
// const DateTimePicker = () => import("@/components/DateTimePicker");
// import paymentService from "@/modules/Payment/service.js";
import Hashids from "hashids";
const hashids = new Hashids();
import moment from "moment";
// import stripeService from "@/modules/Stripe/service.js";
// import DateSelector from "@/components/DateSelector";

export default {
  props: {
    toggleStatus: {
      default: "show",
    },
    CustomerId: {
      default: null,
    },
    DonorId: {
      default: null,
    },
    showAll: {
      default: false,
    },
    due: {
      default: false,
    },
  },
  name: "recurring-invoice-list",
  data() {
    return {
      selectedSubscription: null,
      createDialog: false,
      showExpire: false,
      selectedInvoiceParentPayDue: 0,
      loadingBtn: false,
      selectedCard: null,
      loadingCard: false,
      loadingDialog: false,
      showCustomersOnly: false,
      showDonorsOnly: false,
      toggleChange: "all",
      dateTime: {
        start: this.$moment().startOf("day").toISOString(),
        end: this.$moment().endOf("day").toISOString(),
      },
      headers: [],
      Subscriptions: [],
      search: null,
      loading: true,
      page: 1,
      sortBy: "id",
      pageCount: 0,
      sortDesc: true,
      itemsPerPage: 10,
    };
  },
  components: {
    Create,
  },
  watch: {
    // DonorId: {
    //   immediate: true,
    //   handler() {
    //     this.getList();
    //   },
    // },
    toggleChange(val) {
      if (val === "donors") {
        this.showDonorsOnly = true;
        this.showCustomersOnly = false;
      } else if (val === "parents") {
        this.showCustomersOnly = true;
        this.showDonorsOnly = false;
      } else {
        this.showCustomersOnly = false;
        this.showDonorsOnly = false;
      }
      this.getList();
    },
    showExpire() {
      this.getList();
    },
  },
  created() {
    this.loadHeader();
    this.getList();
    this.$events.listen("subscriptionListEvent", () => this.getList());
    // this.$events.listen("invoiceListEvent", () => this.getList());
    // document.title = "taleem - Invoice List";
  },
  beforeDestroy() {
    this.$events.remove("subscriptionListEvent");
  },
  computed: {
    ...mapGetters("global", [
      "currentUser",
      "checkRightStatus",
      "currentBusiness",
    ]),
    ...mapGetters("customer", ["getCustomer"]),
    ...mapGetters("subscription", ["InvoiceDetails", "getEditInvoiceId"]),
    isEmbedded() {
      return Boolean(this.CustomerId || this.DonorId);
    },
    activeCount() {
      return this.Subscriptions.filter((item) => this.isRecurringExpired(item))
        .length;
    },
    expiringCount() {
      return this.Subscriptions.length - this.activeCount;
    },
    recurringTotal() {
      return this.Subscriptions.filter((item) =>
        this.isRecurringExpired(item)
      ).reduce((total, item) => total + Number(item.total || 0), 0);
    },
  },
  methods: {
    ...mapActions("subscription", ["ResetInvoice"]),
    runTest() {
      console.log("running");
      subscriptionService.createAutoInvoice().then((response) => {
        console.log("response", response);
      });
    },
    formattedNextInvoiceDate(nextInvoiceDate) {
      // console.log('sub.nextInvoiceDate', nextInvoiceDate)
      return moment.utc(nextInvoiceDate).format("MM/DD/YYYY");
    },
    isRecurringExpired(item) {
      if (item.endDate) {
        const endDate = new Date(item.endDate);
        const nextInvoiceDate = new Date(item.nextInvoiceDate);
        if (endDate < nextInvoiceDate) {
          // console.log('endDate > nextInvoiceDate', endDate > nextInvoiceDate, endDate, nextInvoiceDate)
        }

        return endDate > nextInvoiceDate; // Returns true if end date is greater than current date
      }
      return true;
    },
    close() {
      this.reset();
      this.paymentDialog = false;
    },
    async receivePayment(val) {
      this.selectedInvoice = (await subscriptionService.getById(val.id)).data;
      let dueAmount = 0;
      // this.selectedInvoiceParentPayDue = dueAmount
      this.selectedInvoice.InvoiceDetails.map((row) => {
        console.log("rowrowrow", row);
        dueAmount += row.parentPay;
      });
      if (dueAmount === 0) {
        this.Payment.amount = val.due;
      } else {
        this.Payment.amount = dueAmount;
      }
      this.selectedInvoiceParentPayDue = dueAmount;
      this.paymentDialog = !this.paymentDialog;
    },
    add() {
      if (this.DonorId) {
        this.$router.push({
          path: `/subscription/create?DonorId=${this.DonorId}`,
        });
      } else if (this.CustomerId) {
        this.$router.push({
          path: `/subscription/create?CustomerId=${this.CustomerId}`,
        });
      } else {
        this.$router.push({ path: `/subscription/create` });
      }
    },
    hasOnlinePayment(item) {
      return Boolean(
        (item.Customer && item.Customer.payCompassId) ||
          (item.Donor && item.Donor.payCompassId)
      );
    },
    paymentMethodLabel(item) {
      if (item.cashCollect) return "Cash collection";
      if (this.hasOnlinePayment(item)) return "Automatic payment";
      return "Invoice only";
    },
    paymentMethodIcon(item) {
      if (item.cashCollect) return "mdi-cash";
      if (this.hasOnlinePayment(item)) return "mdi-credit-card-outline";
      return "mdi-file-document-outline";
    },
    durationLabel(item) {
      if (item.recurringForever || !item.endDate) return "Runs until cancelled";
      return `Ends ${this.$moment(item.endDate).format("MMM D, YYYY")}`;
    },
    viewSubscription(subscription) {
      const hashId = hashids.encode(subscription.id);
      const routeData = this.$router.resolve({
        path: `/global/subscription/${hashId}/view`,
      });
      window.open(routeData.href, "newwindow", "width=850, height=1100");
    },
    viewProfileNewTab(val) {
      let routeData = null;
      if (val.DonorId) {
        routeData = this.$router.resolve({
          path: `/donors/${val.DonorId}/profile`,
        });
      } else {
        routeData = this.$router.resolve({
          path: `/parents/${val.CustomerId}/profile`,
        });
      }
      window.open(routeData.href, "_blank");
    },
    viewProfile(val) {
      if (val.DonorId) {
        this.$router.push({ path: `/donors/${val.DonorId}/profile` });
      } else {
        this.$router.push({ path: `/parents/${val.CustomerId}/profile` });
      }
    },
    loadHeader() {
      this.headers = [];
      // this.headers.push({
      //   text: 'ID',
      //   align: "left",
      //   value: "id",
      //   sortable: true,
      // });

      // this.headers.push({
      //   text: this.$t("labels.date"),
      //   align: "left",
      //   value: "date",
      //   sortable: true,
      // });

      // this.headers.push({
      //   text: "Starting Date",
      //   value: "startingDate",
      //   align: "left",
      //   sortable: true,
      // });

      this.headers.push(
        {
          text: "Name",
          value: "name",
          align: "left",
          sortable: true,
        },
      {
        text: "Billing Method",
        value: "cashCollect",
        align: "left",
        sortable: false,
        }
      );

      // if (this.DonorId) {
      //   this.headers.push({
      //     text: "Name",
      //     value: "Donor.name",
      //     align: "left",
      //     sortable: true,
      //   });
      // }
      // if (this.CustomerId) {
      //   this.headers.push({
      //     text: "Name",
      //     value: "Customer.name",
      //     align: "left",
      //     sortable: true,
      //   });
      // }

      this.headers.push({
        text: "Next Due Date",
        align: "left",
        value: "nextInvoiceDate",
        sortable: true,
      });

      this.headers.push({
        text: "Schedule",
        align: "left",
        value: "endDate",
        sortable: true,
      });

      this.headers.push({
        text: "Amount",
        value: "total",
        align: "right",
        sortable: true,
      });

      // this.headers.push({
      //   text: "Payment",
      //   value: "payment",
      //   align: "right",
      //   sortable: true,
      // });

      // this.headers.push({
      //   text: "Balance",
      //   value: "due",
      //   align: "right",
      //   sortable: true,
      // });
      this.headers.push({
        text: "",
        value: "action",
        sortable: false,
      });
    },
    getList() {
      this.loading = true;
      let where = {};
      // if (!this.showAll) {
      //   where = {
      //     start: this.dateTime.start,
      //     end: this.dateTime.end,
      //   };
      // }
      if (this.DonorId) {
        where.DonorId = this.DonorId;
      }

      if (this.CustomerId) {
        where.CustomerId = this.CustomerId;
      }

      if (this.due) {
        where.due = true;
      }
      where.showCustomersOnly = this.showCustomersOnly;
      where.showDonorsOnly = this.showDonorsOnly;
      where.showExpire = this.showExpire;
      return subscriptionService.getAll(where).then((response) => {
        this.loading = false;
        this.Subscriptions = response.data;
        // console.log("response.data subscription ", response.data)
        this.Subscriptions.map((subscription) => {
          let total = 0;
          subscription.SubscriptionDetails.map((detail) => {
            if (!detail.deletedAt) total += detail.price * detail.qty;
          });
          subscription.total = total;
          if (subscription.Customer)
            subscription.name = subscription.Customer.name;
          else if (subscription.Donor)
            subscription.name = subscription.Donor.name;
        });
        return response;
      });
    },
    createAutoInvoice() {
      return subscriptionService.createAutoInvoice().then((response) => {
        // this.Subscriptions = response.data;
        console.log("response", response);
        return response;
      });
    },
    editSubscription(subscription) {
      this.$router.push({ path: `/subscription/${subscription.id}/edit` });
    },
    createInvoice(subscription) {
      // console.log('subscription', subscription)
      this.loadingDialog = true;
      return subscriptionService
        .createInvoice(subscription.id)
        .then((response) => {
          // this.Subscriptions = response.data;
          // console.log('response', response)
          this.loadingDialog = false;
          this.getList();
          this.$events.emit("invoiceListEvent");
          return response;
        })
        .catch((err) => {
          this.loadingDialog = false;
          this.$swal({
            title: "Create Invoice",
            text: err.data.message,
            icon: "error",
          });
          console.log("error", err);
        });
    },
    createInvoiceAndCollect(subscription) {
      console.log("subscription", subscription);
      this.loadingDialog = true;
      return subscriptionService
        .createInvoiceAndCollect(subscription.id)
        .then((response) => {
          // this.Subscriptions = response.data;
          console.log("response", response);
          this.loadingDialog = false;
          this.$events.emit("invoiceListEvent");
          return response;
        })
        .catch((err) => {
          this.loadingDialog = false;
          this.$swal({
            title: "Create Invoice",
            text: err.data.message,
            icon: "error",
          });
          console.log("error", err);
        });
    },
    collectedPayment(subscription) {
      console.log("subscription", subscription);
      this.selectedSubscription = subscription;
      this.createDialog = !this.createDialog;
    },
    deleteSubscription(subscription) {
      this.$swal({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#B71C1C",
        cancelButtonColor: "#43A047",
        cancelButtonText: "No",
        confirmButtonText: "Yes",
      }).then((result) => {
        if (result.value) {
          this.loadingDialog = true;
          subscriptionService
            .delete(subscription.id)
            .then((response) => {
              console.log("response delete", response);
              this.loadingDialog = false;
              if (response.data) {
                this.$swal
                  .mixin({
                    toast: true,
                    position: "top-end",
                    showConfirmButton: false,
                    timer: 3000,
                  })
                  .fire({
                    icon: "success",
                    title: "Subscription has been deleted.",
                  });
                this.getList();
              }
            })
            .catch((error) => {
              this.loadingDialog = false;
              this.$swal({
                title: "Delete Subscription",
                text: error.data.message,
                icon: "error",
              });
            });
        }
      });
    },
  },
};
</script>

<style scoped>

.subscription-page {
  min-height: 100%;
  padding: 30px 36px 48px;
  background: #f6f8f6;
}

.subscription-page.embedded {
  padding: 14px;
  background: white;
}

.subscription-page-heading {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 22px;
}

.page-eyebrow {
  margin-bottom: 5px;
  color: #43a047;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.subscription-page-heading h1 {
  margin: 0;
  color: #172033;
  font-size: 29px;
  line-height: 1.2;
  letter-spacing: -0.025em;
}

.subscription-page-heading p {
  margin: 6px 0 0;
  color: #728095;
  font-size: 13px;
}

.subscription-page-heading .v-btn,
.subscription-summary .v-btn {
  height: 42px;
  border-radius: 9px;
  text-transform: none;
  letter-spacing: 0;
}

.subscription-card {
  border: 1px solid #e3e9e3;
  border-radius: 12px !important;
  overflow: hidden;
}

.subscription-summary {
  padding: 16px 20px;
  display: flex;
  align-items: center;
  gap: 40px;
  border-bottom: 1px solid #edf0ed;
}

.summary-item {
  display: flex;
  flex-direction: column;
}

.summary-item strong {
  color: #253047;
  font-size: 18px;
  font-weight: 800;
  line-height: 1.2;
}

.summary-item span {
  margin-top: 2px;
  color: #7b8799;
  font-size: 10px;
}

.subscription-filters {
  padding: 15px 20px;
  display: grid;
  grid-template-columns: minmax(300px, 480px) auto 1fr;
  align-items: center;
  gap: 16px;
  border-bottom: 1px solid #edf0ed;
}

.subscription-filters .v-input--switch {
  justify-self: end;
}

.audience-toggle {
  border: 1px solid #dce5dc;
  border-radius: 8px;
  overflow: hidden;
}

.subscription-row td {
  padding-top: 13px !important;
  padding-bottom: 13px !important;
}

.subscriber-link {
  display: inline-flex;
  align-items: center;
  color: #172033;
  text-align: left;
}

.subscriber-link span {
  display: flex;
  flex-direction: column;
}

.subscriber-link small,
.date-secondary {
  color: #929d95;
  font-size: 0.72rem;
}

.date-primary {
  color: #172033;
  font-weight: 700;
}

.template-total {
  color: #172033;
  font-size: 1rem;
  font-weight: 800;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  color: #526057;
}

.empty-state span {
  color: #929d95;
}

@media (max-width: 800px) {
  .subscription-page {
    padding: 20px 14px 36px;
  }

  .subscription-page.embedded {
    padding: 10px;
  }

  .subscription-page-heading {
    align-items: stretch;
    flex-direction: column;
  }

  .subscription-page-heading .v-btn {
    width: 100%;
  }

  .subscription-summary {
    padding: 14px 18px;
    gap: 26px;
    overflow-x: auto;
  }

  .summary-item {
    flex: 0 0 auto;
  }

  .subscription-filters {
    padding: 16px 18px;
    grid-template-columns: 1fr;
  }

  .subscription-filters .v-input--switch {
    justify-self: start;
  }

  .audience-toggle {
    width: 100%;
  }

  .audience-toggle .v-btn {
    flex: 1;
  }

  .subscription-table >>> thead {
    display: none;
  }

  .subscription-table >>> tbody tr.subscription-row {
    margin: 12px;
    padding: 14px;
    display: grid;
    grid-template-columns: 1fr auto;
    border: 1px solid #e1e8e1;
    border-radius: 10px;
  }

  .subscription-table >>> tbody tr.subscription-row td {
    height: auto;
    padding: 8px 4px !important;
    display: block;
    border: 0 !important;
  }

  .subscription-table >>> tbody tr.subscription-row td::before {
    content: attr(data-label);
    display: block;
    margin-bottom: 3px;
    color: #8a958d;
    font-size: 0.67rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .subscription-table >>> .action-cell {
    grid-column: 2;
    grid-row: 1;
  }
}
</style>
